import { useCallback, useMemo, useState } from "react";
import { Participant, TranscriptSegment } from "@/types";
import { useConversationDetailsContext } from "@/context";
import { useSpeakerAssignment } from "@/hooks/useSpeakerAssignment";
import { UpdateParticipantData } from "@/components/pages/Conversations/ConversationsDetails/ConversationPersonTag/ConversationPersonPopoverPaginated";

export interface UseTranscriptPersonTagReturn {
  participant: Participant | undefined;
  participants: Participant[];
  existingParticipants: Participant[];
  isCurrentSpeakerUpdating: boolean;
  onDeleteSpeaker: (participantId: string) => void;
  onUnassignForPopover: (() => void) | undefined;
  handleSelectFromPopover: (data: UpdateParticipantData) => void;
  handleSpeakerSelect: (
    participantId: string,
    speakerId: string,
    speakerToSwitchId?: string,
  ) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

function getNextSpeakerLetter(transcriptMessages: TranscriptSegment[]): string {
  const existingIds = new Set(transcriptMessages.map((s) => s.speakerId));
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  for (const letter of letters) {
    if (!existingIds.has(letter)) return letter;
  }
  return `${existingIds.size + 1}`;
}

export const useTranscriptPersonTag = (
  segment: TranscriptSegment,
): UseTranscriptPersonTagReturn => {
  const [open, setOpen] = useState(false);

  const {
    conversationId,
    participants,
    isSpeakerUpdating,
    createOrUpdateParticipant,
    segmentUpdating,
    setSelectReassign,
    transcriptMessages,
  } = useConversationDetailsContext();

  const { assignToSpeaker, unassignFromSpeaker, createAndAssign } =
    useSpeakerAssignment();

  const onSelectSpeakerNew = useCallback(
    (participantId: string, speakerId: string) => {
      assignToSpeaker(speakerId, participantId);
    },
    [assignToSpeaker],
  );

  const onDeleteSpeakerNew = useCallback(
    (participantId: string) => {
      const p = participants.find((p) => p.id === participantId);
      if (p?.speakerId) {
        unassignFromSpeaker(p.speakerId);
      }
    },
    [participants, unassignFromSpeaker],
  );

  const handleSelectFromPopover = useCallback(
    (data: UpdateParticipantData) => {
      const speakerId = segment.speakerId;

      const segmentSpeakerIsTaken = participants.some(
        (p) => p.speakerId === speakerId,
      );

      if (data.participantId) {
        const existingParticipant = participants.find(
          (p) => p.id === data.participantId,
        );

        const isNewEntity =
          existingParticipant &&
          data.data?.id &&
          existingParticipant.clientId !== data.data.id &&
          existingParticipant.userId !== data.data.id;

        if (isNewEntity && speakerId && data.type) {
          createAndAssign(speakerId, data.data, data.type).catch(() => {
            // Error toast handled by context mutation's onError
          });
        } else if (
          existingParticipant?.speakerId &&
          existingParticipant.speakerId !== speakerId
        ) {
          setSelectReassign({
            segment,
            participantId: data.participantId,
            speakerId: speakerId!,
            speakerToSwitchId: existingParticipant.speakerId,
          });
        } else if (!existingParticipant?.speakerId && segmentSpeakerIsTaken) {
          const newSpeakerId = getNextSpeakerLetter(transcriptMessages ?? []);
          setSelectReassign({
            segment,
            participantId: data.participantId,
            speakerId: speakerId!,
            speakerToSwitchId: newSpeakerId,
          });
        } else {
          onSelectSpeakerNew(data.participantId, speakerId!);
        }
      } else if (speakerId && data.type) {
        if (segmentSpeakerIsTaken) {
          const newSpeakerId = getNextSpeakerLetter(transcriptMessages ?? []);
          setSelectReassign({
            segment,
            participantId: "",
            speakerId: speakerId,
            speakerToSwitchId: newSpeakerId,
            createData: { data: data.data, type: data.type },
          });
        } else {
          createAndAssign(speakerId, data.data, data.type).catch(() => {
            // Error toast handled by context mutation's onError
          });
        }
      }
      setOpen(false);
    },
    [
      segment,
      participants,
      onSelectSpeakerNew,
      setSelectReassign,
      createAndAssign,
      transcriptMessages,
    ],
  );

  const onSelectSpeakerOld = useCallback(
    (participantId: string, speakerId: string) => {
      createOrUpdateParticipant({
        conversationId,
        id: participantId,
        speakerId,
      });
    },
    [conversationId, createOrUpdateParticipant],
  );

  const handleSpeakerSelect = useCallback(
    (participantId: string, speakerId: string, speakerToSwitchId?: string) => {
      if (participantId && speakerToSwitchId) {
        setSelectReassign({
          segment,
          participantId,
          speakerId,
          speakerToSwitchId,
        });
      } else {
        onSelectSpeakerOld(participantId, speakerId);
      }
      setOpen(false);
    },
    [onSelectSpeakerOld, segment, setSelectReassign],
  );

  const existingParticipants = useMemo(
    () => participants.filter((p) => p.clientId || p.userId),
    [participants],
  );

  const participant = useMemo(
    () => participants.find((p) => p.speakerId === segment.speakerId),
    [participants, segment.speakerId],
  );

  const onDeleteSpeaker = onDeleteSpeakerNew;

  const isCurrentSpeakerUpdating = useMemo(() => {
    return segmentUpdating === segment.id || isSpeakerUpdating;
  }, [segmentUpdating, segment.id, isSpeakerUpdating]);

  const onUnassignForPopover = useMemo(() => {
    if (!participant?.id) return undefined;
    return () => {
      onDeleteSpeakerNew(participant.id);
      setOpen(false);
    };
  }, [participant?.id, onDeleteSpeakerNew]);

  return {
    participant,
    participants,
    existingParticipants,
    isCurrentSpeakerUpdating,
    onDeleteSpeaker,
    onUnassignForPopover,
    handleSelectFromPopover,
    handleSpeakerSelect,
    open,
    setOpen,
  };
};
